import { PropertiesSelectors } from '../selectors';

import CurrentProperty from '../model/CurrentProperty';
import Unit from '../model/Unit';
import ExternalTour from '../model/ExternalTour';
import ContactEmailDeliverability from '../model/ContactEmailDeliverability';
import Freemium from '../model/Freemium';
import PropertyOverview from '../model/PropertyOverview';
import RentGuarantee from '../model/RentGuarantee';

import api from '../utils/api';
import postingUtils from '../utils/postingUtils';

import Constants from '../constants/';

import PropertiesActions from './PropertiesActions';
import { logError } from '@zg-rentals/log-error';

export const ERROR = {
  CONNECTION_ABORTED: 'Connection aborted',
  FILE_SIZE_EXCEEDS_MAX: 'file size exceeds maximum',
  UPLOAD_PHOTO_ERROR: 'Error when uploading photo',
};

/*
 * formatMaxFileSizeMsg
 * @param origMsg
 *
 * @origMsg format must be [message] : [filesize_number]
 */
export function formatMaxFileSizeMsg(origMsg) {
  const msgArr = origMsg.split(':');
  let msg = msgArr[0];
  const value = msgArr[1].trim() / 1024 / 1024 || 0; // convert to mb
  msg = `${msg}: ${value}MB`;

  return msg;
}

const PostingActions = {
  apartment: {
    amenity: {
      create(propertyAmenity) {
        return (dispatch) =>
          dispatch(api.users.properties.apartmentAmenities.create(propertyAmenity))
            .then((result) => result)
            .catch((err) => Promise.reject(err.message));
      },
      delete(amenityId) {
        return (dispatch) =>
          dispatch(api.users.properties.apartmentAmenities.delete(amenityId))
            .then((result) => result)
            .catch((err) => Promise.reject(err.message));
      },
    },
  },
  floorplan: {
    amenity: {
      create(unitAmenity) {
        return (dispatch) =>
          dispatch(api.users.properties.unitAmenities.create(unitAmenity))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return result;
            })
            .catch((errorRMWModel) => {
              logError({
                error: errorRMWModel,
                context: 'PostingActions.floorplan.amenity.create',
              });

              return Promise.reject(errorRMWModel);
            });
      },
      delete(amenityId) {
        return (dispatch) =>
          dispatch(api.users.properties.unitAmenities.delete(amenityId))
            .then((result) => result)
            .catch((err) => Promise.reject(err.message));
      },
    },
    layout: {
      delete(floorPlanId) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.layouts.delete(floorPlanId)).catch((err) =>
            Promise.reject(err.message),
          );
      },
      upload(formData) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.layouts.upload(formData))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }
              if (result.hasOwnProperty('code') && result.code === 'ECONNABORTED') {
                // connection aborted
                errorRMWModel.message = ERROR.CONNECTION_ABORTED;

                return Promise.reject(errorRMWModel);
              }

              return result.response;
            })
            .catch((errorRMWModel) => {
              const msg = errorRMWModel.message;

              // is filesize error
              if (errorRMWModel.message.toLowerCase().indexOf(ERROR.FILE_SIZE_EXCEEDS_MAX) > -1) {
                errorRMWModel.message = formatMaxFileSizeMsg(errorRMWModel.message);
              }

              errorRMWModel.message = msg;

              // do not dispatch via NewRelicService. the components will have better context

              return Promise.reject(errorRMWModel);
            });
      },
    },
    photo: {
      caption(photoId, caption) {
        const photoObj = {
          photoId,
          caption,
        };

        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.photos.caption(photoObj)).catch((err) =>
            Promise.reject(err.message),
          );
      },
      delete(photoId) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.photos.delete(photoId)).catch((err) => Promise.reject(err.message));
      },
      upload(formData) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.photos.upload(formData))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }
              if (result.hasOwnProperty('code') && result.code === 'ECONNABORTED') {
                // connection aborted
                errorRMWModel.message = ERROR.CONNECTION_ABORTED;

                return Promise.reject(errorRMWModel);
              }

              return result.response;
            })
            .catch((errorRMWModel) => {
              // is filesize error
              if (errorRMWModel.message.toLowerCase().indexOf(ERROR.FILE_SIZE_EXCEEDS_MAX) > -1) {
                errorRMWModel.message = formatMaxFileSizeMsg(errorRMWModel.message);
              }

              // do not dispatch via NewRelicService. the components will have better context

              return Promise.reject(errorRMWModel);
            });
      },
    },
    get(floorplanId) {
      return (dispatch) =>
        dispatch(api.users.properties.floorPlans.get(floorplanId))
          .then((result) => result.response)
          .catch((err) => Promise.reject(err.message));
    },
  },
  property: {
    amenity: {
      create(propertyAmenity, componentGroupName = '') {
        return (dispatch) =>
          dispatch(api.users.properties.amenities.create(propertyAmenity))
            .then((result) => {
              if (result.success === false) {
                return Promise.reject(result.error);
              }

              return result;
            })
            .catch((errorRMWModel) => {
              logError({
                error: errorRMWModel,
                context: 'PostingActions.property.amenity.create',
              });

              return Promise.reject(errorRMWModel);
            });
      },
      delete(amenityId) {
        return (dispatch) =>
          dispatch(api.users.properties.amenities.delete(amenityId))
            .then((result) => result)
            .catch((err) => Promise.reject(err.message));
      },
    },
    deactivate(propertyId, reason = null, comment = null) {
      return (dispatch) =>
        dispatch(api.users.properties.deactivate(propertyId, reason, comment))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }
            return result;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.property.deactivate',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    create(formData) {
      return (dispatch) => {
        const dataForApi = postingUtils.transformPropertyListingTypeForApi(formData);

        return dispatch(api.users.properties.create(dataForApi))
          .then((result) => {
            const { error: errorRMWModel, success } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }
            const { propertyId } = result.response;

            // TODO: get details with listingAlias
            return dispatch(PostingActions.property.getDetails(propertyId));
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.property.create',
            });

            return Promise.reject(errorRMWModel);
          });
      };
    },
    delete(propertyId, reason) {
      return (dispatch) =>
        dispatch(api.users.properties.delete(propertyId, reason))
          .then((result) => result)
          .catch((err) => Promise.reject(err.message));
    },
    markAcknowledgedLandlordMessage(actionId) {
      return (dispatch) =>
        dispatch(api.users.properties.markAcknowledgedLandlordMessage(actionId))
          .then((response) => {
            if (response.success && response.response) {
              return response.response;
            }
            return Promise.reject(response.error);
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.markAcknowledgedLandlordMessage',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    // add boolean for restrictions
    getDetails(propertyId) {
      return this.getDetailsInternal(api.users.properties.getDetails(propertyId));
    },
    getDetailsInternal(dispatchedApi) {
      return (dispatch) =>
        dispatch(dispatchedApi)
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;
            let dataForForm = {};
            let mergedListingAndTransformedData = {};

            if (success) {
              dataForForm = postingUtils.transformDatafromApi(response);
              dataForForm.propertyId = dataForForm.listingId;

              const freemiumData = response?.freemium || {};

              mergedListingAndTransformedData = {
                ...dataForForm,
                freemium: freemiumData,
              };

              /**
               * there is an issue where CurrentProperty.rentalProtection gets reset.
               * this is because we merge all of the properties back into CurrentProperty

               *
               * a fix on the FE is to remove the rentGuarantee key from the api response
               * and set the value onto the rentalProtection key
               *
               * the long term solution would be for the BE to return rentalProtection key from the api response
               */
              mergedListingAndTransformedData.rentalProtection = mergedListingAndTransformedData.rentGuarantee;

              delete mergedListingAndTransformedData.rentGuarantee;

              const currentPropertyModel = CurrentProperty(mergedListingAndTransformedData);

              dispatch(PropertiesActions.updateCurrentProperty(currentPropertyModel));
            } else {
              // error
              return Promise.reject({
                ...errorRMWModel,
                message: 'could not load the property details',
              });
            }

            return mergedListingAndTransformedData;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.getDetails',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    getDetailsOverview(listingAlias, includeRestrictions = false) {
      return (dispatch, getState) =>
        dispatch(api.users.properties.getDetailsOverview(listingAlias, includeRestrictions))
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }

            const currentProperty = PropertiesSelectors.makeCurrentProperty(getState());

            const propertyOverview = PropertyOverview(response);

            dispatch({
              type: Constants.UPDATE_PROPERTY_OVERVIEW,
              payload: propertyOverview,
            });

            dispatch(
              PropertiesActions.updateCurrentProperty({
                contactEmailDeliverability: ContactEmailDeliverability(response.contactEmailDeliverability),
                active: response.status === 'LISTED FOR RENT',
                freemium: Freemium(response.freemium),
                street: response.address.street,
                city: response.address.city,
                state: response.address.state,
                listingTypeCode: response.listingType.code,
                zip: response.address.zip,
                unit: response.address.unit,
                isMultifamily: response.isMultifamily,
                isArchived: response.isArchived,
                restrictions: {
                  ...currentProperty.restrictions,
                  ...response?.restrictions,
                  isLeasesRestricted: response.isLeasesRestricted,
                  isLeaseBuilderRestricted: response.isLeaseBuilderRestricted,
                },
                listingAlias: response.listingAlias,
                listingAvailability: response.listingAvailability || {},
                rentalProtection: RentGuarantee(response.rentGuarantee),
                updateActiveStatus: response.updateActiveStatus,
              }),
            );

            return response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.getRestrictions',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    getStatus(propertyId) {
      return (dispatch) =>
        dispatch(api.users.properties.getStatus(propertyId))
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }

            // sync with current property
            const { expirationDate, dateLastActivated } = response;

            dispatch(
              PropertiesActions.updateCurrentProperty({
                dateLastActivated,
                expirationDate,
              }),
            );

            return response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.getStatus',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    edit(formData, { componentGroupName = '', preserveFieldName = false, reserveErrorObject = false } = {}) {
      return (dispatch) => {
        let dataToSave;

        if (formData.fieldName === 'isApplicationsAccepted') {
          formData.value = formData.value || false;
        }

        if (preserveFieldName === false) {
          const mapFormFieldNameToApiFieldName = postingUtils.mapFormDataToApi(formData.fieldName);

          if (mapFormFieldNameToApiFieldName === undefined) {
            console.error('ERROR: field is not found in mapFormDataToApi', formData.fieldName);

            return Promise.reject();
          }

          dataToSave = {
            fieldName: mapFormFieldNameToApiFieldName,
            [mapFormFieldNameToApiFieldName]: formData.value,
            ...(formData.additionalParams && { additionalParams: formData.additionalParams }),
            propertyId: formData.propertyId,
          };
        } else {
          const { fieldName, additionalParams } = formData;

          dataToSave = {
            fieldName,
            [fieldName]: formData.value,
            ...(additionalParams && { additionalParams }),
            propertyId: formData.propertyId,
          };
        }

        // Handle an issue on the API side when an empty value is provided.
        if (formData.fieldName === 'contactEmail' && formData.value === '') {
          return Promise.reject('Please enter a valid email address.');
        }

        return dispatch(api.users.properties.edit(dataToSave))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }

            return result;
          })
          .catch((err) => {
            return Promise.reject(reserveErrorObject ? err : err.message);
          });
      };
    },
    pets(dataToSave) {
      return (dispatch) =>
        dispatch(api.users.properties.pets(dataToSave))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }

            return result;
          })
          .catch((err) => Promise.reject(err.message));
    },
    modularListingProgress(params) {
      return (dispatch, getState) => {
        if (getState?.()?.properties?.currentProperty?.isFeed) {
          return;
        }

        return dispatch(api.users.properties.modularListingProgress(params))
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;

            if (!success) {
              return Promise.reject({
                ...errorRMWModel,
                message: 'Could not save progress.  Please try again.',
              });
            }

            dispatch(
              PropertiesActions.updateCurrentProperty({
                modularListingProgress: response,
              }),
            );

            return response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.modularListingProgress',
            });

            return Promise.reject(errorRMWModel);
          });
      };
    },
    unit: {
      create(floorPlanId, params) {
        const mappedParams = Object.keys(params).reduce((acc, key) => {
          const backendKey = postingUtils.mapFormDataToApi(`floorplans.${key}`);
          acc[backendKey] = params[key];
          return acc;
        }, {});
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.units.createV2(floorPlanId, mappedParams)).then((result) => {
            if (result.error) {
              return Promise.reject(result);
            }
            return Unit(result.response);
          });
      },
    },
    externalTour: {
      associateModel(externalTour, encodedAlias, shouldAssociate, encodedFloorplanKey) {
        return (dispatch) =>
          dispatch(
            api.users.properties.externalTour.associateModel(
              externalTour,
              encodedAlias,
              shouldAssociate,
              encodedFloorplanKey,
            ),
          )
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return ExternalTour(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
      associateListing(externalTour, encodedAlias, shouldAssociate) {
        return (dispatch) =>
          dispatch(api.users.properties.externalTour.associateListing(externalTour, encodedAlias, shouldAssociate))
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return ExternalTour(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
    },
    removeFloorplan(floorplanId) {
      return (dispatch) =>
        dispatch(api.users.properties.floorPlans.delete(floorplanId))
          .then((result) => {
            const { error: errorRMWModel, success } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }

            return result.response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.property.removeFloorplan',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    confirmVerification(contactData) {
      return (dispatch) =>
        dispatch(api.users.contacts.verifier.confirm(contactData))
          .then((result) => result)
          .catch((err) => Promise.reject(err.message));
    },
    requestVerification(contactData) {
      return (dispatch) =>
        dispatch(api.users.contacts.verifier.send(contactData))
          .then((result) => result)
          .catch((err) => Promise.reject(err.message));
    },
    convertFloorplanOrUnitToNucleus(modelId, unit) {
      return (dispatch) =>
        dispatch(api.users.properties.convertFloorplanOrUnitToNucleus(modelId, unit))
          .then((result) => {
            if (result.success) {
              return result.response;
            }

            return Promise.reject(result.error);
          })
          .catch((err) => Promise.reject(err));
    },
  },
};

export default PostingActions;
